import { useCallback, useEffect, useState } from 'react';
import type { SessionStatus, UsageView } from '@filmsloth/shared';
import { MatchOverlay } from './components/MatchOverlay.js';
import { OfflineBanner } from './components/OfflineBanner.js';
import { CreateScreen } from './screens/CreateScreen.js';
import { FinishedScreen } from './screens/FinishedScreen.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { JoinScreen } from './screens/JoinScreen.js';
import { LobbyScreen } from './screens/LobbyScreen.js';
import { ResultsScreen } from './screens/ResultsScreen.js';
import { RunoffScreen } from './screens/RunoffScreen.js';
import { SwipeScreen } from './screens/SwipeScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { FilmRecordScreen } from './screens/FilmRecordScreen.js';
import { WorkshopScreen } from './screens/WorkshopScreen.js';
import { WatchlistScreen } from './screens/WatchlistScreen.js';
import { ImpressumScreen } from './screens/ImpressumScreen.js';
import { AboutScreen } from './screens/AboutScreen.js';
import { ContactScreen } from './screens/ContactScreen.js';
import { PrivacyScreen } from './screens/PrivacyScreen.js';
import { DeviceScreen } from './screens/DeviceScreen.js';
import { StatsScreen } from './screens/StatsScreen.js';
import { api } from './api/client.js';
import { useDocumentMeta } from './lib/documentMeta.js';
import { isContentView, pathForView, viewForPath } from './lib/routes.js';
import { useT } from './i18n/index.js';
import { readStartView } from './lib/startView.js';
import { reportView } from './lib/usage.js';
import { useWakeLock } from './lib/useWakeLock.js';
import { COMMUNITY_CATALOG } from './lib/features.js';
import { useAccount } from './session/useAccount.js';
import { useResults } from './session/useResults.js';
import { useSession } from './session/useSession.js';

type LandingView =
  | 'home'
  | 'create'
  | 'join'
  | 'workshop'
  | 'watchlist'
  | 'login'
  | 'account'
  | 'film'
  | 'about'
  | 'contact'
  | 'privacy'
  | 'impressum'
  // Settings for this phone. Deliberately without an address of its own: it
  // is a switch inside the app, not a page anybody links to or crawls.
  | 'device'
  // How much the site is being used. Same treatment as `device` and for a
  // stronger reason: it is the operator's own instrument panel, and a page
  // with an address is a page that gets crawled and linked.
  | 'stats';

/** The in-session screens, named the way the usage counters name them. */
const USAGE_VIEW_BY_STATUS: Record<SessionStatus, UsageView> = {
  lobby: 'lobby',
  swiping: 'swipe',
  runoff: 'runoff',
  finished: 'finished',
};

/** Invite links look like `https://…/?code=ABC123`. */
function readInviteCode(): string {
  return new URLSearchParams(window.location.search).get('code')?.toUpperCase() ?? '';
}

/** Confirmation mails link back as `https://…/?confirm=<token>`. */
function readConfirmToken(): string {
  return new URLSearchParams(window.location.search).get('confirm') ?? '';
}

export function App() {
  const t = useT();
  const session = useSession();
  const account = useAccount();
  const [inviteCode] = useState(readInviteCode);
  const [confirmToken] = useState(readConfirmToken);
  // A standing page's address wins over the query string: `/privacy` is a
  // page somebody asked for, while `?code=`, `?confirm=` and `?view=` are
  // errands the app runs at `/`.
  const [view, setView] = useState<LandingView>(
    () => viewForPath(window.location.pathname) ?? readStartView(window.location.search),
  );
  const [openFilmId, setOpenFilmId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  /**
   * Moves between landing views, keeping the address bar honest.
   *
   * Only the standing pages have an address of their own; every other view is
   * a step inside the app and lives at `/`. Pushing rather than replacing is
   * what makes the browser's back button walk back out of a page it walked
   * into — the one thing people expect from something that looks like a link.
   */
  const go = useCallback((next: LandingView) => {
    setView(next);
    const path = pathForView(next);
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
  }, []);

  // ...and the back button has to move the app, not just the address bar.
  useEffect(() => {
    const onPopState = () => setView(viewForPath(window.location.pathname) ?? 'home');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Content pages write their own title, description and canonical link;
  // everything else is the app, which has one of each.
  useDocumentMeta(
    isContentView(view)
      ? null
      : { title: t('meta.title'), description: t('meta.description'), view: 'home' },
  );

  const inSession = Boolean(session.state);

  // Hold the screen on for as long as the evening is actually running. Not on
  // the finished screen: the decision is made, and a phone that refuses to
  // dim after that is just a phone with a flat battery.
  const sessionStatus = session.state?.status ?? null;
  useWakeLock(
    sessionStatus === 'lobby' || sessionStatus === 'swiping' || sessionStatus === 'runoff',
  );

  const results = useResults(
    session.credentials?.token ?? null,
    showResults,
    session.state?.version ?? 0,
  );

  // A confirmation link is spent the moment it is opened. Doing it here rather
  // than on the account screen means the link works whatever the person's
  // session looks like, including in a browser that was never signed in.
  useEffect(() => {
    if (!confirmToken) return;
    void api
      .confirmEmail(confirmToken)
      .then(() => account.refresh())
      .catch(() => {
        // An expired or already-spent link is not worth an error screen: the
        // account page below says plainly whether the address is confirmed.
      })
      .finally(() => window.history.replaceState(null, '', window.location.pathname));
    // Deliberately keyed on the token alone. `account` is in the body only to
    // refresh it afterwards, and listing it here would re-run the effect on
    // the very state change it causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmToken]);

  // An app shortcut has done its job the moment the screen is open; leaving
  // `?view=` in the address bar would reopen it on every reload, including
  // long after somebody has navigated somewhere else.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('view')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // Once you are in a session the invite parameter has done its job.
  useEffect(() => {
    if (inSession && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [inSession]);

  /**
   * What is actually on screen, in the vocabulary of the usage counters.
   *
   * Derived here rather than reported from each screen, because the screens
   * are chosen in several places below — a session's status, the results
   * overlay, the landing view — and a `reportView` call in each of them is a
   * call somebody will forget to add to the next one. `null` while the
   * session is still being restored: nothing is on screen yet.
   */
  const usageView: UsageView | null = session.restoring
    ? null
    : session.state
      ? showResults
        ? 'results'
        : USAGE_VIEW_BY_STATUS[session.state.status]
      : view;

  useEffect(() => {
    if (usageView) reportView(usageView);
  }, [usageView]);

  // A new screen starts at its top. The app is one page, so without this the
  // scroll position carries over: the start button sits at the foot of the
  // lobby, and the deck then opened with its header scrolled out of sight.
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [usageView]);

  if (session.restoring) {
    return (
      <div className="app">
        <OfflineBanner />
        <div className="screen center">
          <p className="muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!session.state) {
    const back = () => go('home');
    // Anywhere that needs an account sends people here rather than refusing
    // them: the workshop is the one part of the site a login is required for,
    // and being told that is more useful than a locked door.
    const toLogin = () => go('login');

    return (
      <div className="app">
        <OfflineBanner />
        {session.removed ? (
          <p className="banner banner--warn" role="status">
            {t('group.removedBanner')}
          </p>
        ) : null}
        {session.error ? <p className="banner banner--error">{session.error}</p> : null}
        {view === 'about' ? (
          <AboutScreen onBack={back} onNavigate={go} />
        ) : view === 'contact' ? (
          <ContactScreen onBack={back} onNavigate={go} />
        ) : view === 'privacy' ? (
          <PrivacyScreen onBack={back} onNavigate={go} />
        ) : view === 'impressum' ? (
          <ImpressumScreen onBack={back} onNavigate={go} />
        ) : view === 'device' ? (
          <DeviceScreen onBack={back} />
        ) : view === 'stats' ? (
          <StatsScreen onBack={back} />
        ) : COMMUNITY_CATALOG && view === 'workshop' ? (
          <WorkshopScreen
            onBack={back}
            onOpenFilm={(filmId) => {
              setOpenFilmId(filmId);
              go('film');
            }}
            onNeedLogin={toLogin}
          />
        ) : COMMUNITY_CATALOG && view === 'film' && openFilmId ? (
          <FilmRecordScreen
            filmId={openFilmId}
            onBack={() => go('workshop')}
            onNeedLogin={toLogin}
          />
        ) : view === 'login' ? (
          <LoginScreen onBack={back} onDone={() => go('account')} />
        ) : view === 'account' ? (
          <AccountScreen onBack={back} onOpenStats={() => go('stats')} />
        ) : view === 'watchlist' ? (
          <WatchlistScreen onBack={back} />
        ) : view === 'create' ? (
          <CreateScreen onBack={back} onCreated={session.enter} />
        ) : view === 'join' ? (
          <JoinScreen initialCode={inviteCode} onBack={back} onJoined={session.enter} />
        ) : (
          <HomeScreen
            onCreate={() => go('create')}
            onJoin={() => go('join')}
            onWorkshop={() => go('workshop')}
            onWatchlist={() => go('watchlist')}
            onAccount={() => go(account.account ? 'account' : 'login')}
            onNavigate={go}
            onDeviceSettings={() => go('device')}
          />
        )}
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="app">
        <OfflineBanner />
        <ResultsScreen
          results={results.results}
          loading={results.loading}
          error={results.error}
          onClose={() => setShowResults(false)}
          onReload={results.reload}
        />
      </div>
    );
  }

  const hostName =
    session.state.participants.find((participant) => participant.id === session.state?.hostId)
      ?.name ?? '';

  return (
    <div className="app">
      <OfflineBanner />
      {session.state.status === 'lobby' ? <LobbyScreen /> : null}
      {session.state.status === 'swiping' ? (
        <SwipeScreen onShowResults={() => setShowResults(true)} />
      ) : null}
      {session.state.status === 'runoff' ? <RunoffScreen /> : null}
      {session.state.status === 'finished' ? (
        <FinishedScreen
          finalPick={session.state.finalPick}
          country={session.state.filters.country}
          sessionDurationMinutes={
            session.state.finalPick
              ? (session.state.updatedAt - session.state.createdAt) / 60000
              : null
          }
          nextRoundCode={session.state.nextRoundCode}
          onShowResults={() => setShowResults(true)}
          onNextRound={() => void session.nextRound()}
          onNewSession={() => {
            session.reset();
            go('home');
          }}
        />
      ) : null}

      {session.pendingMatch && session.state.status !== 'finished' ? (
        <MatchOverlay
          movie={session.pendingMatch}
          country={session.state.filters.country}
          isHost={session.isHost}
          hostName={hostName}
          onWatch={() => void session.decide('watch')}
          onContinue={() => void session.decide('continue')}
        />
      ) : null}
    </div>
  );
}
